/**
 * Hook for managing category templates
 */

import { useCallback } from 'react';
import { logger } from '@/lib/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  categoryTemplateService,
  type CategoryTemplate,
  type DomainType,
  type CreateCategoryTemplateDto,
  type UpdateCategoryTemplateDto,
} from '@/services/categoryTemplateService';
import { usePlatformAdmin } from './usePlatformAdmin';

export function useCategoryTemplates(domain?: DomainType) {
  const queryClient = useQueryClient();
  const { canManageCategories } = usePlatformAdmin();

  // Fetch categories for a domain
  const {
    data: categories,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['category-templates', domain],
    queryFn: () => (domain ? categoryTemplateService.getByDomain(domain) : Promise.resolve([])),
    enabled: !!domain,
    staleTime: 0, // Always fetch fresh data
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Create category mutation
  const createMutation = useMutation({
    mutationFn: (dto: CreateCategoryTemplateDto) => categoryTemplateService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      toast.success('Category created successfully');
    },
    onError: error => {
      logger.error('Failed to create category:', error);
      toast.error('Failed to create category');
    },
  });

  // Update category mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateCategoryTemplateDto }) => {
      logger.log('Updating category via mutation:', { id, updates });
      return categoryTemplateService.update(id, updates);
    },
    onSuccess: (data) => {
      logger.log('Category update successful:', {
        id: data.id,
        hasIcon: !!data.icon_svg,
        iconLength: data.icon_svg?.length,
      });
      // Force cache invalidation and immediate refetch
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      queryClient.refetchQueries({ queryKey: ['category-templates'] });
      toast.success('Category updated successfully');
    },
    onError: error => {
      logger.error('Failed to update category:', error);
      toast.error('Failed to update category');
    },
  });

  // Delete category mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryTemplateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      toast.success('Category deleted successfully');
    },
    onError: error => {
      logger.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    },
  });

  // Update icon mutation
  const updateIconMutation = useMutation({
    mutationFn: ({ id, svgContent }: { id: string; svgContent: string }) =>
      categoryTemplateService.updateIcon(id, svgContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      toast.success('Icon updated successfully');
    },
    onError: error => {
      logger.error('Failed to update icon:', error);
      toast.error('Failed to update icon');
    },
  });

  // Update color mutation
  const updateColorMutation = useMutation({
    mutationFn: ({ id, color }: { id: string; color: string }) =>
      categoryTemplateService.updateColor(id, color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      toast.success('Color updated successfully');
    },
    onError: error => {
      logger.error('Failed to update color:', error);
      toast.error('Failed to update color');
    },
  });

  // Reorder categories
  const reorderMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; display_order: number }>) =>
      categoryTemplateService.reorder(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-templates'] });
      toast.success('Categories reordered successfully');
    },
    onError: error => {
      logger.error('Failed to reorder categories:', error);
      toast.error('Failed to reorder categories');
    },
  });

  // Get categories grouped by parent
  const getCategoryTree = useCallback(() => {
    if (!categories) return new Map();

    const tree = new Map<string | null, CategoryTemplate[]>();

    // First pass: group by parent
    categories.forEach(cat => {
      const parent = cat.subcategory ? cat.category : null;
      if (!tree.has(parent)) {
        tree.set(parent, []);
      }
      tree.get(parent)!.push(cat);
    });

    return tree;
  }, [categories]);

  // Get subcategories for a specific category
  const getSubcategories = useCallback(
    (category: string) => {
      if (!categories) return [];
      return categories.filter(cat => cat.category === category && cat.subcategory);
    },
    [categories],
  );

  // Get main categories (no subcategory)
  const getMainCategories = useCallback(() => {
    if (!categories) return [];
    return categories.filter(cat => !cat.subcategory);
  }, [categories]);

  return {
    // Data
    categories,
    categoryTree: getCategoryTree(),
    mainCategories: getMainCategories(),

    // Loading states
    isLoading,
    error,

    // Actions (only available for platform admins)
    createCategory: canManageCategories ? createMutation.mutate : undefined,
    updateCategory: canManageCategories ? updateMutation.mutate : undefined,
    deleteCategory: canManageCategories ? deleteMutation.mutate : undefined,
    updateIcon: canManageCategories ? updateIconMutation.mutate : undefined,
    updateColor: canManageCategories ? updateColorMutation.mutate : undefined,
    reorderCategories: canManageCategories ? reorderMutation.mutate : undefined,

    // Utility functions
    getSubcategories,
    refetch,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Hook for fetching subcategories of a specific category
export function useSubcategories(domain: DomainType, category: string) {
  const {
    data: subcategories,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['category-templates', domain, category, 'subcategories'],
    queryFn: () => categoryTemplateService.getSubcategories(domain, category),
    enabled: !!domain && !!category,
  });

  return {
    subcategories,
    isLoading,
    error,
  };
}
